import React from 'react'
import { Trans } from 'react-i18next'
import type { HistoricalReport } from '../../../shared/domain/viewableReports/types.no-deps'
import { displayDate } from '../../../shared/lib/date.no-deps'
import type { CommonDeps } from '../base/CommonDeps'

interface ReportProps extends React.RefAttributes<HTMLDivElement> {
    report: HistoricalReport
    translatedReportTitle?: string
    onClick?: () => void
    commonDeps: CommonDeps
}

type ReportTileProps = React.PropsWithChildren<ReportProps>

/** A wide tile showing a report's id, name, created date and author.
 *
 * If the tile has children, then it shows a chevron icon and can be clicked to expand to show those children.
 *
 * https://www.figma.com/file/0abUs0eJtrAq3aeYRdCoGi/Actions?node-id=4353%3A630148&mode=dev
 */
export function ReportTile({ report, translatedReportTitle, commonDeps, children, ...props }: ReportTileProps) {
    const [isOpen, setIsOpen] = React.useState(false)
    const canExpand = !props.onClick && !!children
    const chevronDirection = isOpen ? 'up' : 'down'

    // TODO: in React 18, use the `useId` hook
    const titleId = `report-title-${report.reportViewData.reportId}`
    const title = (
        <zaptic-ellipsis>
            <span id={titleId} className="AppCore__report__Tile__ReportTitle">
                {report.report.isConfidential && (
                    <zaptic-icon style={{ fontSize: '1.2rem', verticalAlign: 'middle' }} icon-name="lock" />
                )}
                <span className="AppCore__report__Tile__ReportId">#{report.reportViewData.reportId} - </span>
                {translatedReportTitle || report.reportViewData.title}
                {translatedReportTitle && (
                    <span className="AppCore__report__Tile__TranslatedLabel">
                        {' '}
                        (<Trans i18nKey="appcore:common.translated" />)
                    </span>
                )}
            </span>
        </zaptic-ellipsis>
    )
    const submittedDate = displayDate(commonDeps.language, report.report.submittedDate, {
        dateStyle: 'long',
        timeStyle: 'short',
    })
    const CreatedBy: React.FC = () => (
        <>
            <zaptic-profile-circle
                name={report.reportAuthor.name}
                color={report.reportAuthor.profileCircleColor}
                profile-image-url={report.reportAuthor.profileImageUrl}
            />
            {report.reportAuthor.name}
        </>
    )
    const creationInfo = (
        <zaptic-ellipsis>
            <span className="AppCore__report__Tile__DateAndUser">
                <Trans
                    i18nKey="appcore:ReportTile.createdBy"
                    values={{ createdDate: submittedDate }}
                    components={{ CreatedBy: <CreatedBy /> }}
                />
            </span>
        </zaptic-ellipsis>
    )

    const onClick: React.MouseEventHandler<HTMLDivElement> = props.onClick
        ? props.onClick
        : canExpand
          ? () => setIsOpen((open) => !open)
          : undefined
    const ariaExpanded: boolean = canExpand ? isOpen : undefined

    return (
        <div className="AppCore__report__Tile" {...props} aria-labelledby={titleId} aria-expanded={ariaExpanded}>
            <div className="AppCore__report__Tile__CoreContent" onClick={onClick}>
                <div className="AppCore__report__Tile__CoreContent__Icon">
                    <zaptic-icon icon-name="report" />
                </div>
                <div className="AppCore__report__Tile__CoreContent__Text">
                    {title}
                    {creationInfo}
                </div>
                {canExpand && (
                    <zaptic-rotating-chevron
                        class="AppCore__report__Tile__CoreContent__Chevron"
                        direction={chevronDirection}
                    />
                )}
            </div>
            {isOpen && children}
        </div>
    )
}
